"use client";

import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import { AlertTriangle, Trash2, Undo2 } from "lucide-react";
import { Dialog } from "./Dialog";
import { Button } from "./Button";
import { Input } from "./Input";

export type ConfirmTone = "danger" | "neutral";

interface ConfirmDialogProps {
  open: boolean;
  onCancel: () => void;
  /** May be async. The dialog shows a spinner and stays open until it settles. */
  onConfirm: () => void | Promise<void>;
  title: string;
  /** What is about to happen, in one or two sentences. */
  description?: ReactNode;
  /** The thing being acted on, shown verbatim so the reader can check it. */
  subject?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /**
   * When set, the confirm button stays disabled until the reader types this
   * string exactly. Reserve it for the deletes that are worth the friction --
   * a definition that other screens read, not a row in a list.
   */
  requireTypedName?: string;
  /**
   * Shown as a quiet reassurance under the buttons. Pass this when the delete
   * is reversible, so the reader is not made to hesitate over something the
   * recycle bin will hand straight back.
   */
  recoverable?: boolean;
}

/**
 * The one confirmation dialog in the app.
 *
 * It exists because on 2026-09-22 a single unguarded trash icon on
 * /dashboard/entities/types hard-deleted an entity type with no confirmation,
 * no undo and no trace -- the row was gone from the database and nothing had
 * recorded that it ever existed. The row was recoverable only because an
 * orphaned alias happened to survive the delete.
 *
 * Three layers now stand between a click and a loss, and this is the first:
 *   1. this dialog, and for a definition, typing its name
 *   2. soft delete in the database, so DELETE marks rather than destroys
 *   3. instance_events, so even a purge leaves a full snapshot behind
 */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  description,
  subject,
  confirmLabel = "מחק",
  cancelLabel = "ביטול",
  tone = "danger",
  requireTypedName,
  recoverable = true,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fieldId = useId();

  // Reset between openings, so a previous attempt never pre-arms the next one.
  useEffect(() => {
    if (open) {
      setTyped("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const nameMatches = !requireTypedName || typed.trim() === requireTypedName.trim();

  const handleConfirm = useCallback(async () => {
    if (!nameMatches || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      // A unique-key collision is the one failure worth explaining in place:
      // it means the name is still taken by something sitting in the bin.
      const msg = e instanceof Error ? e.message : String(e);
      setError(
        /duplicate key|23505/i.test(msg)
          ? "השם הזה עדיין תפוס בידי פריט שנמצא בסל המיחזור. שחזר אותו או מחק אותו לצמיתות משם."
          : msg
      );
      setBusy(false);
      return;
    }
    setBusy(false);
  }, [nameMatches, busy, onConfirm]);

  // בזמן שהמחיקה רצה סגירה בטעות תשאיר את המשתמש בלי תשובה,
  // ולכן החלון פשוט אינו נסגר עד שהפעולה חוזרת.
  const handleClose = useCallback(() => {
    if (!busy) onCancel();
  }, [busy, onCancel]);

  return (
    <Dialog open={open} onClose={handleClose} title={title}>
      <div className="space-y-4">
        <div className="flex gap-3">
          <div
            className={[
              "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
              tone === "danger"
                ? "bg-[var(--error)]/15 text-red-400"
                : "bg-white/5 text-slate-400",
            ].join(" ")}
          >
            {tone === "danger" ? <AlertTriangle size={16} /> : <Trash2 size={16} />}
          </div>

          <div className="min-w-0 flex-1 space-y-2">
            {description && (
              <p className="text-[13px] leading-relaxed text-slate-300">{description}</p>
            )}
            {subject && (
              <p className="truncate rounded-md border border-white/[0.08] bg-white/[0.03] px-2.5 py-1.5 text-[13px] font-medium text-slate-200">
                {subject}
              </p>
            )}
          </div>
        </div>

        {requireTypedName && (
          <div className="space-y-1.5">
            <label htmlFor={fieldId} className="block text-[12px] text-slate-400">
              הקלד <span className="font-mono text-slate-200">{requireTypedName}</span> כדי לאשר
            </label>
            <Input
              id={fieldId}
              value={typed}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              error={typed.length > 0 && !nameMatches}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && nameMatches) void handleConfirm();
              }}
              placeholder={requireTypedName}
            />
          </div>
        )}

        {error && (
          <p className="rounded-md border border-red-500/20 bg-[var(--error)]/10 px-2.5 py-2 text-[12px] leading-relaxed text-red-300">
            {error}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "secondary"}
            size="sm"
            icon={Trash2}
            loading={busy}
            disabled={!nameMatches}
            onClick={() => void handleConfirm()}
          >
            {confirmLabel}
          </Button>
        </div>

        {recoverable && (
          <p className="flex items-center justify-end gap-1.5 text-[11px] text-slate-500">
            <Undo2 size={11} />
            ניתן לשחזור מסל המיחזור
          </p>
        )}
      </div>
    </Dialog>
  );
}
